import React, { useState, useEffect } from 'react';
import axios from 'axios';
import DOMPurify from 'dompurify';
import SEOHead from '../components/SEOHead';
import { SiteHeader, SiteFooter, FooterCTA, MobileBookBar } from '../components/SiteLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Sanitiser config — allow lightweight inline formatting that legal pages typically use.
const PURIFY_CONFIG = {
  ALLOWED_TAGS: ['a', 'strong', 'em', 'br', 'li', 'ul', 'ol', 'p', 'span'],
  ALLOWED_ATTR: ['href', 'class', 'target', 'rel'],
  // Block javascript: and data: URLs from sneaking into href
  ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel):|[^a-z]|[a-z+.-]+(?:[^a-z+.-:]|$))/i,
};

function LegalPageLayout({ slug, fallbackTitle }) {
  const [page, setPage] = useState(null);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      axios.get(`${API_URL}/content/legal-pages/${slug}`).then(r => r.data).catch(() => null),
      axios.get(`${API_URL}/content/settings`).then(r => r.data).catch(() => ({})),
    ]).then(([pageData, settingsData]) => {
      setPage(pageData);
      setSettings(settingsData);
    }).finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return <div className="min-h-screen bg-[#0F1419] flex items-center justify-center"><div className="text-[#C9A961] font-serif text-lg">Loading...</div></div>;
  }

  const title = page?.title || fallbackTitle;
  const sections = page?.sections || [];
  const lastUpdated = page?.last_updated || 'February 2026';

  const renderContent = (text) => {
    if (!text) return null;
    return text.split('\n').map((line, i) => {
      if (!line.trim()) return <br key={i} />;
      const formatted = line
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
        .replace(/\[(.*?)\]\((.*?)\)/g, '<a href="$2" class="text-[#C9A961] underline" target="_blank" rel="noopener noreferrer">$1</a>');
      if (line.trim().startsWith('- ')) {
        return <li key={i} className="ml-4 list-disc" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(formatted.replace(/^- /, ''), PURIFY_CONFIG) }} />;
      }
      return <p key={i} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(formatted, PURIFY_CONFIG) }} />;
    });
  };

  return (
    <div className="min-h-screen bg-[#0F1419] text-[#E8E4DC]">
      <SEOHead
        title={`${title} | Wentworth House Hotel`}
        description={page?.seo_description || `${title} for Wentworth House Hotel, Bath. Read our ${title.toLowerCase()} for staying at our Georgian B&B.`}
        path={`/${slug}`}
        settings={settings}
      />

      <SiteHeader />

      {/* Hero */}
      <section className="relative pt-[72px]">
        <div className="bg-gradient-to-b from-[#1a1f2e] to-[#0F1419] py-16 sm:py-20 px-4 sm:px-6">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-serif text-[#E8E4DC] mb-3" data-testid={`${slug}-title`}>{title}</h1>
            <div className="w-24 h-[2px] bg-gradient-to-r from-transparent via-[#C9A961] to-transparent mx-auto my-4" />
            <p className="text-sm text-[#E8E4DC]/40">Last updated: {lastUpdated}</p>
          </div>
        </div>
      </section>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <div className="space-y-8 text-sm sm:text-base leading-relaxed text-[#E8E4DC]/70">
          {sections.map((section, i) => (
            <section key={i}>
              <h2 className="text-lg font-semibold text-[#C9A961] mb-3">{section.heading}</h2>
              <div className="space-y-2">{renderContent(section.content)}</div>
            </section>
          ))}
        </div>
      </div>

      <FooterCTA />
      <SiteFooter settings={settings} />
      <MobileBookBar />
    </div>
  );
}

export function PrivacyPolicy() {
  return <LegalPageLayout slug="privacy-policy" fallbackTitle="Privacy Policy" />;
}

export function TermsOfService() {
  return <LegalPageLayout slug="terms-of-service" fallbackTitle="Terms & Conditions" />;
}

export function CookiePolicy() {
  return <LegalPageLayout slug="cookie-policy" fallbackTitle="Cookie Policy" />;
}
